import { Alert, Stack } from "@mui/material";
import { useMemo } from "react";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { Fetching } from "components/common/loading/fetching";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  DateRangeFilterBar,
  useDateRangeFilters,
} from "components/manageLabellers/dateRangeFilterBar";
import { CopyWorkerStatsToClipboardButton } from "components/manageLabellers/copyStatsToClipboardButton";
import {
  buildReviewerBaseColumns,
  buildReviewerBaseCsvColumns,
  REVIEWER_NAME_FIELD,
  ReviewerBaseRow,
} from "components/manageLabellers/reviewerOverviewColumns";
import {
  sumNullable,
  TenantLabellerStatsLoader,
  TenantName,
  TenantStatsState,
  useTenantLabellerStats,
} from "./TenantLabellerStatsLoader";

const mergeReviewerStats = (
  stateByTenant: Record<TenantName, TenantStatsState>,
  activeTenantNames: readonly string[]
): ReviewerBaseRow[] => {
  const merged = new Map<string, ReviewerBaseRow>();
  for (const tenantName of activeTenantNames) {
    const state = stateByTenant[tenantName];
    if (!state) continue;
    for (const entry of state.response?.reviewer_stats ?? []) {
      if (entry.reviewer.labeller_type !== "human") continue;
      const existing = merged.get(entry.reviewer.id);
      merged.set(
        entry.reviewer.id,
        existing
          ? {
              ...existing,
              total_time_reviewed_seconds:
                existing.total_time_reviewed_seconds +
                entry.total_time_reviewed_seconds,
              total_time_spent_reviewing_seconds: sumNullable(
                existing.total_time_spent_reviewing_seconds,
                entry.total_time_spent_reviewing_seconds
              ),
              total_submissions:
                existing.total_submissions + entry.total_submissions,
              periods_reviewed:
                existing.periods_reviewed + entry.periods_reviewed,
            }
          : {
              id: entry.reviewer.id,
              reviewer: entry.reviewer,
              total_time_reviewed_seconds: entry.total_time_reviewed_seconds,
              total_time_spent_reviewing_seconds:
                entry.total_time_spent_reviewing_seconds,
              total_submissions: entry.total_submissions,
              periods_reviewed: entry.periods_reviewed,
            }
      );
    }
  }
  return [...merged.values()];
};

type CrossTenantReviewerOverviewTabProps = Readonly<{
  tenantNames: readonly string[];
}>;

export const CrossTenantReviewerOverviewTab = ({
  tenantNames,
}: CrossTenantReviewerOverviewTabProps) => {
  const { t } = useTenantTranslation();
  const formatNumber1dp = useFormatNumber()(1);
  const dateFilters = useDateRangeFilters();

  const {
    stateByTenant,
    anyLoading,
    anyFetching,
    failedTenantNames,
    onTenantStatsLoaded,
  } = useTenantLabellerStats(tenantNames);

  const rows = useMemo(
    () => mergeReviewerStats(stateByTenant, tenantNames),
    [stateByTenant, tenantNames]
  );

  const columns = useMemo(
    () => buildReviewerBaseColumns(t, formatNumber1dp),
    [t, formatNumber1dp]
  );

  const csvColumns = useMemo(() => buildReviewerBaseCsvColumns(t), [t]);

  return (
    <Stack spacing={2} sx={{ flex: 1, overflow: "hidden" }}>
      {failedTenantNames.length > 0 ? (
        <Alert severity="warning">
          {t("partialTenantResults", { tenants: failedTenantNames.join(", ") })}
        </Alert>
      ) : null}
      {tenantNames.map((tenantName) => (
        <TenantLabellerStatsLoader
          key={tenantName}
          tenantName={tenantName}
          dateFilters={dateFilters}
          onLoaded={onTenantStatsLoaded}
        />
      ))}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ pt: 1 }}>
        <DateRangeFilterBar filters={dateFilters} />
        <CopyWorkerStatsToClipboardButton rows={rows} columns={csvColumns} />
      </Stack>
      <Fetching fetching={anyFetching} sx={{ flex: 1, overflow: "hidden" }}>
        <HotkeyCompliantDataGrid
          columns={columns}
          rows={rows}
          loading={anyLoading}
          initialState={{
            sorting: {
              sortModel: [{ field: REVIEWER_NAME_FIELD, sort: "asc" }],
            },
          }}
          slotProps={{
            loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" },
          }}
          rowHeight={40}
          columnHeaderHeight={44}
          disableRowSelectionOnClick
        />
      </Fetching>
    </Stack>
  );
};
